/**
 * Narrow UPC-A barcode for release meta. 11–12 digits; check digit computed if needed.
 */

import { cn } from '@/lib/utils';

const L = [
  '0001101',
  '0011001',
  '0010011',
  '0111101',
  '0100011',
  '0110001',
  '0101111',
  '0111011',
  '0110111',
  '0001011',
] as const;

const R = [
  '1110010',
  '1100110',
  '1101100',
  '1000010',
  '1011100',
  '1001110',
  '1010000',
  '1000100',
  '1001000',
  '1110100',
] as const;

function checkDigit(eleven: string): number {
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    const n = Number(eleven[i]);
    sum += i % 2 === 0 ? n * 3 : n;
  }
  return (10 - (sum % 10)) % 10;
}

function normalizeUpc(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12) return digits;
  if (digits.length === 11) return digits + String(checkDigit(digits));
  if (digits.length > 12) return digits.slice(0, 12);
  return null;
}

function encodeUpcA(code: string): string {
  let pattern = '101';
  for (let i = 0; i < 6; i++) pattern += L[Number(code[i])];
  pattern += '01010';
  for (let i = 6; i < 12; i++) pattern += R[Number(code[i])];
  pattern += '101';
  return pattern;
}

type Props = {
  upc?: string | null;
  className?: string;
  /** When true, only the bar graphic is shown (digits rendered elsewhere). */
  hideDigits?: boolean;
};

/** UPC-A bit length is fixed at 95 modules. */
const MODULES = 95;

export function UpcBarcode({ upc, className, hideDigits = false }: Props) {
  const code = upc ? normalizeUpc(upc) : null;
  if (!code) return null;

  const bits = encodeUpcA(code);

  return (
    <div
      className={cn('upc-barcode', hideDigits && 'upc-barcode-bars-only', className)}
      aria-label={`UPC ${code}`}
      title={code}
    >
      <svg
        className="upc-barcode-svg"
        viewBox={`0 0 ${MODULES} 20`}
        preserveAspectRatio="none"
        role="img"
        aria-hidden
      >
        {bits.split('').map((bit, i) =>
          bit === '1' ? (
            <rect key={i} x={i} y={0} width={1} height={20} fill="currentColor" />
          ) : null,
        )}
      </svg>
      {!hideDigits ? <span className="upc-digits">{code}</span> : null}
    </div>
  );
}
